import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { siteData } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (title) => {
  if (/address/i.test(title)) return MapPin;
  if (/sales/i.test(title)) return Mail;
  return Phone;
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 20000 });
      setStatus({
        state: "success",
        msg: res.data?.message || "Thanks! Your message has been sent.",
      });
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus({ state: "idle", msg: "" }), 6000);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: detail });
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#080b14]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <span className="accent-bar mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
            {siteData.contact.heading}
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
            {siteData.contact.intro}
          </p>

          <div className="mt-8 space-y-5">
            {siteData.contact.columns.map((c, i) => {
              const Icon = iconFor(c.title);
              return (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-blue-500/10 text-blue-300 flex items-center justify-center flex-shrink-0 border border-blue-400/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{c.title}</p>
                    {c.lines.map((l, k) => (
                      <p key={k} className="text-white/65 text-sm">{l}</p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={submit} className="bg-white/[0.03] rounded-2xl border border-white/10 p-6 md:p-8 shadow-2xl backdrop-blur">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-white/85">Name</label>
              <input
                required
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-md bg-[#0b1220] border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm text-white placeholder-white/40 transition-colors"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/85">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-md bg-[#0b1220] border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm text-white placeholder-white/40 transition-colors"
                placeholder="you@company.com"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm font-medium text-white/85">How can we help?</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full px-3 py-2.5 rounded-md bg-[#0b1220] border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm text-white placeholder-white/40 resize-none transition-colors"
              placeholder="Tell us about your setup, project, or issue…"
            />
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            <p className="text-xs text-white/50">We’ll reply within 1 business day.</p>
            <button
              type="submit"
              disabled={status.state === "loading"}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white font-semibold text-sm hover:bg-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.state === "loading" ? (
                <>
                  Sending… <Loader2 className="w-4 h-4 animate-spin" />
                </>
              ) : (
                <>
                  Send message <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
          {status.state === "success" && (
            <p className="mt-4 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">
              {status.msg}
            </p>
          )}
          {status.state === "error" && (
            <p className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {status.msg}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
